import { ThemedStyledProps } from "styled-components";

import { isTouchDevice } from "@/utils/getters";

import Variants from "./Variants";

export type IThemeProps<P = {}> = ThemedStyledProps<P, ITheme>;

// Font Size
// The font concept has to be improved in future
// Duplicate values can be overwritten at tens digit h3x0 (e.g. h310)
export const typography: ThemeInterface.ITypography = {
  h100: "10px",
  h200: "11px",
  h300: "12px",
  h310: "12px",
  h320: "12px",
  h330: "12px",
  h400: "14px",
  h410: "14px",
  h420: "14px",
  h500: "16px",
  h600: "18px",
  h610: "18px",
  h700: "22px",
  h710: "22px",
  h720: "22px",
  h800: "24px",
  h900: "52px",
};

// Element stack orders
export const zIndex = {
  h100: 1,
  h200: 9,
  h300: 10,
  h500: 1000,
  h900: 2147483647,
};

// Designer specific brand sizes
export const dimensions = {
  maxInteractionWidth: "580px",
  minInteractionHeight: "470px",
  isTouch: isTouchDevice(),
};

// Customized logical definitions
export const thesis: ThemeInterface.IThesis = {
  hasBoxShadow: true,
  hasButtonInvert: false,
  hasIntroBackground: true,
  hasTransparency: true,
  hasPrimaryPrio: false,
  hasAnswerColorEffect: false,
  hasExtendedThumbsHeader: true,
  hasExtraThumbsPadding: false,
  hasThumbsHoverEffect: true,
};

// Media queries specs
export const breakpoints = {
  mobile: `(min-width: 0px) and (max-width: 480px)`,
  mobileLandscape: `(min-width: 481px) and (max-width: 767px)`,
  tablet: `(min-width: 768px) and (max-width: 1024px)`,
  tabletLandscape: `(min-width: 768px) and (max-width: 1024px) and (orientation: landscape)`,
  desktop: `(min-width: 1025px) and (max-width: 3840px)`,
};

// Element shadows
export const shadows = {
  s100: `none`,
  s200: `0 3px 6px 5px rgba(0, 0, 0, 0.5)`,
  s300: `0 5px 20px 0 rgba(0, 0, 0, 0.1)`,
  s400: `0px 8px 24px rgba(0, 0, 0, 0.1)`,
};

const themes: Record<string, ITheme> = {
  // Generic Styling
  default: {
    globals: {
      fontFamily: `Ubuntu, sans-serif`,
      fontFamily200: `Ubuntu, sans-serif`,
      fontFamily400: `Ubuntu Condensed, sans-serif`,
      fontFamily600: `Ubuntu, sans-serif`,
      fontFamilyExtra: `Ubuntu, sans-serif`,
      fontFamilyExtra2: `Ubuntu, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.default,
  },

  // COBI Styling
  cobi: {
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans Light", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.cobi,
  },

  // AUBI Styling
  aubi: {
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans Light", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.aubi,
  },

  // spobi Styling (Sport Bild)
  spobi: {
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"AntennaCondBold", "Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"AntennaCondBold", "Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.spobi,
  },

  // BILD Styling
  bild: {
    globals: {
      fontFamily: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily200: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily400: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily600: `"Gotham", Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra: `"Gotham", Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra2: `"GothamCondensed", Helvetica, Tahoma, sans-serif`,
      fontWeightBold: 900,
      ...dimensions,
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h100: "12px",
      h200: "14px",
      h310: "21px",
      h320: "18px",
      h330: "14px",
      h400: "14px",
      h410: "15px",
      h420: "16px",
      h610: "20px",
      h710: "36px",
      h720: "16px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: true,
      hasTransparency: false,
      hasPrimaryPrio: true,
      hasAnswerColorEffect: true,
      hasExtendedThumbsHeader: false,
      hasExtraThumbsPadding: false,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `0 3px 6px rgba(0, 0, 0, 0.29)`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.bild,
  },

  // BZ Styling
  bz: {
    globals: {
      fontFamily: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily200: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily400: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily600: `"Gotham", Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra: `"Gotham", Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra2: `"GothamCondensed", Helvetica, Tahoma, sans-serif`,
      fontWeightBold: 900,
      ...dimensions,
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h100: "12px",
      h200: "14px",
      h310: "21px",
      h320: "18px",
      h330: "14px",
      h400: "14px",
      h410: "15px",
      h420: "16px",
      h610: "20px",
      h710: "36px",
      h720: "16px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: true,
      hasTransparency: false,
      hasPrimaryPrio: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `0 3px 6px rgba(0, 0, 0, 0.29)`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.bild,
  },

  // Bild-Fitbook Styling (also for BRANDSTUDIO)
  bild_fitbook: {
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans Light", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.bild_fitbook,
  },

  // Bild-Stylebook Styling (also for BRANDSTUDIO)
  bild_stylebook: {
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans Light", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.bild_stylebook,
  },

  // Bild-Techbook Styling (also for BRANDSTUDIO)
  bild_techbook: {
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans Light", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.bild_techbook,
  },

  // Bild-Travelbook Styling (also for BRANDSTUDIO)
  bild_travelbook: {
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans Light", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.bild_travelbook,
  },

  // Bild-MyHomebook Styling (also for BRANDSTUDIO)
  bild_myhomebook: {
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans Light", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.bild_myhomebook,
  },

  // BRANDSTUDIO Bild Brand Story Styling
  bild_brandstory: {
    globals: {
      fontFamily: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily200: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily400: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily600: `"Gotham", Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra: `"Gotham", Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra2: `"GothamCondensed", Helvetica, Tahoma, sans-serif`,
      fontWeightBold: 900,
      ...dimensions,
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h100: "12px",
      h200: "14px",
      h310: "21px",
      h320: "18px",
      h410: "15px",
      h610: "20px",
      h710: "36px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: true,
      hasTransparency: false,
      hasPrimaryPrio: true,
      hasAnswerColorEffect: true,
      hasExtendedThumbsHeader: false,
      hasExtraThumbsPadding: false,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `0 3px 6px rgba(0, 0, 0, 0.29)`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.bild,
  },

  // BRANDSTUDIO Bild Product Story Styling
  bild_productstory: {
    globals: {
      fontFamily: `"LucidaGrande", Lucida Grande,Lucida Sans Unicode,Lucida Sans,Geneva,Verdana,sans-serif`,
      fontFamily200: `"LucidaGrande", Lucida Grande,Lucida Sans Unicode,Lucida Sans,Geneva,Verdana,sans-serif`,
      fontFamily400: `"LucidaGrande", Lucida Grande,Lucida Sans Unicode,Lucida Sans,Geneva,Verdana,sans-serif`,
      fontFamily600: `"LucidaGrande", Lucida Grande,Lucida Sans Unicode,Lucida Sans,Geneva,Verdana,sans-serif`,
      fontFamilyExtra: `"LucidaGrande", Lucida Grande,Lucida Sans Unicode,Lucida Sans,Geneva,Verdana,sans-serif`,
      fontFamilyExtra2: `"LucidaGrande", Lucida Grande,Lucida Sans Unicode,Lucida Sans,Geneva,Verdana,sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h100: "12px",
      h200: "14px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: true,
      hasTransparency: false,
      hasPrimaryPrio: true,
      hasAnswerColorEffect: true,
      hasExtendedThumbsHeader: false,
      hasExtraThumbsPadding: false,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `0 3px 6px rgba(0, 0, 0, 0.29)`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.bild,
  },

  // WELT Styling
  welt: {
    globals: {
      fontFamily: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily200: `ffmark Condensed, "Helvetica Neue", Helvetica,Arial, sans-serif`,
      fontFamily400: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily600: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra2: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
      maxInteractionWidth: "620px",
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h700: "18px",
      h720: "20px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: false,
      hasThumbsHoverEffect: false,
      hasButtonInvert: true,
      hasExtendedThumbsHeader: false,
      hasExtraThumbsPadding: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.welt,
  },

  // BRANDSTUDIO WELT Brandstory Styling
  welt_brandstory: {
    globals: {
      fontFamily: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily200: `ffmark Condensed, "Helvetica Neue", Helvetica,Arial, sans-serif`,
      fontFamily400: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily600: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra2: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
      maxInteractionWidth: "620px",
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h700: "18px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: false,
      hasThumbsHoverEffect: false,
      hasButtonInvert: true,
      hasExtendedThumbsHeader: false,
      hasExtraThumbsPadding: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.welt,
  },

  // BRANDSTUDIO WELT Brandstory Styling
  welt_productstory: {
    globals: {
      fontFamily: `"Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily200: `"Helvetica Neue", Helvetica,Arial, sans-serif`,
      fontFamily400: `"Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily600: `"Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra: `"Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra2: `"Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
      maxInteractionWidth: "620px",
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h700: "18px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: false,
      hasThumbsHoverEffect: false,
      hasButtonInvert: true,
      hasExtendedThumbsHeader: false,
      hasExtraThumbsPadding: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.welt,
  },

  // BRANDSTUDIO MANschaftsbus Styling
  manschaftsbus: {
    globals: {
      fontFamily: `Helvetica`,
      fontFamily200: `Helvetica`,
      fontFamily400: `Helvetica`,
      fontFamily600: `Helvetica`,
      fontFamilyExtra: `Helvetica`,
      fontFamilyExtra2: `Helvetica`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.default,
  },

  // ICON Styling
  icon: {
    globals: {
      fontFamily: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily200: `ffmark Condensed, "Helvetica Neue", Helvetica,Arial, sans-serif`,
      fontFamily400: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily600: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra2: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
      maxInteractionWidth: "620px",
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h700: "18px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: false,
      hasThumbsHoverEffect: false,
      hasButtonInvert: true,
      hasExtendedThumbsHeader: false,
      hasExtraThumbsPadding: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.icon,
  },

  // MEDIA_PIONEER Styling
  mediapioneer: {
    globals: {
      fontFamily: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamily200: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamily400: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamily600: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra2: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontWeightBold: 700,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `0px 0px 4px 0px rgba(93, 45, 188, 0.5)`,
    }),
    zIndex,
    ...Variants.mediapioneer,
  },

  // MUSIKEXPRESS Styling
  musikexpress: {
    globals: {
      fontFamily: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamily200: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamily400: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamily600: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra2: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontWeightBold: 700,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: true,
      hasButtonInvert: false,
      hasPrimaryPrio: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.musikexpress,
  },

  rollingstone: {
    globals: {
      fontFamily: `Tahoma, Helvetica, sans-serif`,
      fontFamily200: `Tahoma, Helvetica, sans-serif`,
      fontFamily400: `Tahoma, Helvetica, sans-serif`,
      fontFamily600: `Tahoma, Helvetica, sans-serif`,
      fontFamilyExtra: `Tahoma, Helvetica, sans-serif`,
      fontFamilyExtra2: `Tahoma, Helvetica, sans-serif`,
      fontWeightBold: 700,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: true,
      hasButtonInvert: false,
      hasPrimaryPrio: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.rollingstone,
  },

  metalhammer: {
    globals: {
      fontFamily: `Arvo, Helvetica, Tahoma, sans-serif`,
      fontFamily200: `Arvo, Helvetica, Tahoma, sans-serif`,
      fontFamily400: `Arvo, Helvetica, Tahoma, sans-serif`,
      fontFamily600: `Arvo, Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra: `Arvo, Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra2: `Arvo, Helvetica, Tahoma, sans-serif`,
      fontWeightBold: 700,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: true,
      hasButtonInvert: false,
      hasPrimaryPrio: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.metalhammer,
  },
};

export { themes };
